import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Section } from "design-system/molecules/Section"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { useState } from "react"
import { useFormContext, type UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CampaignTriggerTypeEnum, type Flow } from "schema"
import { z } from "zod"

import { getCompatibleTriggers } from "modules/campaigns/lib/getCompatibleTriggers"
import { ChannelSelect } from "./ChannelSelect"
import { FlowTypeSelector } from "./FlowTypeSelector"
import { useTriggerKind } from "./hooks/useTriggerKind"
import { TriggerSelect } from "./TriggerSelect"

type Props = {
  isEditable: boolean
}

type CampaignFormValues = z.infer<typeof UpdateCampaignSchemaFrontend>
type CampaignForm = UseFormReturn<CampaignFormValues>

function resetConfigurationFields(form: CampaignForm) {
  const opts = { shouldDirty: true } as const
  form.setValue("trigger", null, opts)
  form.setValue("channel", null, opts)
  form.setValue("triggerIntegration", null, opts)
  form.setValue("goals", [], opts)
  form.setValue("templates", [], opts)
  form.setValue("openingMessages", [], opts)
  form.setValue("whatsappNumberId", null, opts)
}

// Returns true if any configuration field that `resetConfigurationFields`
// would clear is currently set. Used to decide whether a flow-change needs
// the confirmation dialog — an untouched setup has nothing to lose.
function hasConfigurationToLose(form: CampaignForm): boolean {
  const values = form.getValues()
  return Boolean(
    values.trigger ||
    values.channel ||
    values.triggerIntegration ||
    (values.goals && values.goals.length > 0) ||
    (values.templates && values.templates.length > 0) ||
    (values.openingMessages && values.openingMessages.length > 0) ||
    values.whatsappNumberId,
  )
}

export function SetupSubTab({ isEditable }: Props) {
  const { t } = useTranslation()
  const form = useFormContext<CampaignFormValues>()
  const { items: triggerItems } = useTriggerKind()
  const flow = form.watch("flow")
  const channel = form.watch("channel")
  const trigger = form.watch("trigger")

  const isWhatsappDefault = trigger === CampaignTriggerTypeEnum.WhatsappDefault
  const canEditSetup = isEditable && !isWhatsappDefault

  const [pendingFlow, setPendingFlow] = useState<Flow | null>(null)

  const activeCompatibleTriggers = getCompatibleTriggers(channel, flow).filter(
    (tr) => triggerItems[tr]?.active !== false,
  )
  const hasNoCompatibleTriggers = Boolean(channel) && activeCompatibleTriggers.length === 0

  function handleFlowSelect(value: Flow) {
    if (value === flow) return
    if (!hasConfigurationToLose(form)) {
      form.setValue("flow", value, { shouldDirty: true })
      return
    }
    setPendingFlow(value)
  }

  function handleConfirmFlowChange() {
    if (!pendingFlow) return
    resetConfigurationFields(form)
    form.setValue("flow", pendingFlow, { shouldDirty: true })
    setPendingFlow(null)
  }

  function handleCancelFlowChange() {
    setPendingFlow(null)
  }

  return (
    <>
      <div className="flex flex-col gap-8">
        <Section
          title={t("campaigns.configuration.setup.flow.title")}
          description={t("campaigns.configuration.setup.flow.description")}
          className="gap-3"
        >
          <FlowTypeSelector
            value={flow ?? undefined}
            onChange={handleFlowSelect}
            disabled={!canEditSetup}
          />
        </Section>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <Section
            title={t("campaigns.configuration.setup.channel.title")}
            description={t("campaigns.configuration.setup.channel.description")}
            className="gap-3 h-full"
          >
            <ChannelSelect isEditable={Boolean(canEditSetup && flow)} />
          </Section>
          <Section
            title={t("campaigns.configuration.setup.trigger.title")}
            description={t("campaigns.configuration.setup.trigger.description")}
            className="gap-3 h-full"
          >
            {hasNoCompatibleTriggers ? (
              <div className="flex items-center px-3 py-3 rounded-md border border-border bg-muted/30">
                <p className="text-sm text-muted-foreground">
                  {t("campaigns.configuration.setup.trigger.noTriggersForChannel")}
                </p>
              </div>
            ) : (
              <TriggerSelect isEditable={Boolean(canEditSetup && channel)} />
            )}
          </Section>
        </div>
      </div>
      <Dialog
        open={pendingFlow !== null}
        onOpenChange={(open) => {
          if (!open) handleCancelFlowChange()
        }}
      >
        <DialogContent size="md">
          <DialogHeader className="flex flex-col gap-4">
            <DialogTitle>{t("campaigns.configuration.setup.flow.changeDialog.title")}</DialogTitle>
            <DialogDescription>
              {t("campaigns.configuration.setup.flow.changeDialog.description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-4 gap-2">
            <Button variant="outline" onClick={handleCancelFlowChange}>
              {t("campaigns.configuration.setup.flow.changeDialog.cancel")}
            </Button>
            <Button onClick={handleConfirmFlowChange}>
              {t("campaigns.configuration.setup.flow.changeDialog.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
